import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { device } from '../../styles/responsive';

const CircleWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 10;
  mix-blend-mode: screen;
`;

const Circle = styled(motion.div)`
  position: absolute;
  width: ${props => props.size};
  height: ${props => props.size};
  background: #07CDCD;
  border-radius: 50%;
  left: ${props => props.left};
  top: ${props => props.top};
  transform: translate(-50%, -50%);
  display: ${props => props.showOnMobile ? 'none' : 'block'};
  
  @media ${device.xl} {
    width: ${props => props.sizeXl || props.size};
    height: ${props => props.sizeXl || props.size};
    display: ${props => props.showOnMobile ? 'none' : 'block'};
  }
  
  @media ${device.lg} {
    width: ${props => props.sizeLg || props.sizeXl || props.size};
    height: ${props => props.sizeLg || props.sizeXl || props.size};
    display: ${props => props.showOnMobile ? 'none' : 'block'};
  }
  
  @media ${device.md} {
    width: ${props => props.sizeMd || props.sizeLg || props.sizeXl || props.size};
    height: ${props => props.sizeMd || props.sizeLg || props.sizeXl || props.size};
    left: ${props => props.leftMd || props.left};
    top: ${props => props.topMd || props.top};
    border-radius: ${props => props.mobileShape || '50%'};
    display: ${props => props.showOnMobile ? 'block' : 'none'};
  }
  
  @media ${device.sm} {
    width: ${props => props.sizeSm || props.sizeMd || props.sizeLg || props.sizeXl || props.size};
    height: ${props => props.sizeSm || props.sizeMd || props.sizeLg || props.sizeXl || props.size};
    left: ${props => props.leftSm || props.leftMd || props.left};
    top: ${props => props.topSm || props.topMd || props.top};
    border-radius: ${props => props.mobileShape || '50%'};
    display: ${props => props.showOnMobile ? 'block' : 'none'};
  }
`;

const AnimatedCircles = () => {
  // Desktop circles (original)
  const circles = [
    // Smallest circle to the left
    { 
      size: '50px', 
      sizeMd: '40px',
      sizeSm: '30px',
      left: '20rem', 
      leftMd: '8rem',
      leftSm: '4rem',
      top: '350px', 
      topMd: '300px',
      topSm: '250px',
      delay: 0,
      duration: 3.5,
      startY: -10,
      moveY: [-10, -30, -10],
      zIndex: 0,
      showOnMobile: false
    },
    // Circle above the 'w' in Software
    { 
      size: '150px',
      sizeMd: '100px',
      sizeSm: '70px',
      left: '61rem', 
      leftMd: '80%',
      leftSm: '75%',
      top: 'calc(50% - 270px)', 
      topMd: 'calc(40% - 150px)',
      topSm: '25%',
      delay: 0.2,
      duration: 4.2,
      startY: 0,
      moveY: [0, -25, 0],
      showOnMobile: false
    },
    // Circle covering part of 'n' in Engineer
    { 
      size: '200px',
      sizeMd: '120px',
      sizeSm: '80px',
      left: '530px', 
      leftMd: '40%',
      leftSm: '35%',
      top: 'calc(50% + 85px)', 
      topMd: '60%',
      topSm: '55%',
      delay: 0.3,
      duration: 3.8,
      startY: -15,
      moveY: [-15, -35, -15],
      showOnMobile: false
    },
    // Biggest circle behind 'r' in Engineer
    { 
      size: '340px',
      sizeMd: '200px',
      sizeSm: '140px',
      left: 'calc(50% + 350px)', 
      leftMd: '80%',
      leftSm: '75%',
      top: '450px',
      topMd: '70%',
      topSm: '75%',
      delay: 0.4,
      duration: 4.5,
      startY: -5,
      moveY: [-5, -20, -5],
      behind: true,
      showOnMobile: false
    }
  ];
  
  // Mobile-only random circles
  const mobileCircles = [
    // Top-left circle
    {
      sizeSm: '45px',
      leftSm: '15%',
      topSm: '15%',
      delay: 0.1,
      duration: 3.2,
      startY: -5,
      moveY: [-5, -15, -5],
      showOnMobile: true
    },
    // Top-right circle
    {
      sizeSm: '60px',
      leftSm: '85%',
      topSm: '12%',
      delay: 0.3,
      duration: 3.6,
      startY: -8,
      moveY: [-8, -18, -8],
      showOnMobile: true
    },
    // Left-middle circle
    {
      sizeSm: '35px',
      leftSm: '10%',
      topSm: '40%',
      delay: 0.5,
      duration: 3.4,
      startY: -10,
      moveY: [-10, -20, -10],
      showOnMobile: true
    },
    // Right-middle circle
    {
      sizeSm: '50px',
      leftSm: '88%',
      topSm: '45%',
      delay: 0.2,
      duration: 3.8,
      startY: -7,
      moveY: [-7, -17, -7],
      showOnMobile: true
    },
    // Center-ish circle
    {
      sizeSm: '65px',
      leftSm: '65%',
      topSm: '38%',
      delay: 0.7,
      duration: 4.0,
      startY: -12,
      moveY: [-12, -24, -12],
      showOnMobile: true
    },
    // Bottom-middle circle
    {
      sizeSm: '55px',
      leftSm: '50%',
      topSm: '85%',
      delay: 0.4,
      duration: 3.5,
      startY: -8,
      moveY: [-8, -20, -8],
      showOnMobile: true
    },
    // Bottom-left circle
    {
      sizeSm: '40px',
      leftSm: '22%',
      topSm: '78%',
      delay: 0.6,
      duration: 3.7,
      startY: -6,
      moveY: [-6, -16, -6],
      showOnMobile: true
    }
  ];

  return (
    <CircleWrapper>
      {circles.map((circle, index) => (
        <Circle
          key={index}
          size={circle.size}
          sizeXl={circle.sizeXl}
          sizeLg={circle.sizeLg}
          sizeMd={circle.sizeMd}
          sizeSm={circle.sizeSm}
          left={circle.left}
          leftMd={circle.leftMd}
          leftSm={circle.leftSm}
          top={circle.top}
          topMd={circle.topMd}
          topSm={circle.topSm}
          mobileShape={circle.mobileShape}
          showOnMobile={circle.showOnMobile}
          initial={{ opacity: 0, scale: 0.8, y: circle.startY }}
          animate={{ 
            opacity: 1,
            scale: 1,
            y: circle.moveY,
          }}
          transition={{
            opacity: { duration: 1, delay: 1.5 + circle.delay },
            scale: { duration: 0.8, delay: 1.5 + circle.delay },
            y: {
              duration: circle.duration,
              repeat: Infinity,
              delay: 1.5 + circle.delay,
              ease: "easeInOut"
            }
          }}
          style={{
            zIndex: circle.behind ? -1 : 0,
            pointerEvents: 'none'
          }}
        />
      ))}
      
      {mobileCircles.map((circle, index) => (
        <Circle
          key={`mobile-circle-${index}`}
          size={circle.size || '50px'}
          sizeXl={circle.sizeXl}
          sizeLg={circle.sizeLg}
          sizeMd={circle.sizeMd}
          sizeSm={circle.sizeSm}
          left={circle.left || '50%'}
          leftMd={circle.leftMd}
          leftSm={circle.leftSm}
          top={circle.top || '50%'}
          topMd={circle.topMd}
          topSm={circle.topSm}
          mobileShape={circle.mobileShape}
          showOnMobile={circle.showOnMobile}
          initial={{ opacity: 0, scale: 0.8, y: circle.startY }}
          animate={{ 
            opacity: 1,
            scale: 1,
            y: circle.moveY,
          }}
          transition={{
            opacity: { duration: 1, delay: 1.5 + circle.delay },
            scale: { duration: 0.8, delay: 1.5 + circle.delay },
            y: {
              duration: circle.duration,
              repeat: Infinity,
              delay: 1.5 + circle.delay,
              ease: "easeInOut"
            }
          }}
          style={{
            zIndex: circle.behind ? -1 : 0,
            pointerEvents: 'none',
            filter: 'drop-shadow(0 0 8px rgba(7, 205, 205, 0.3))'
          }}
        />
      ))}
    </CircleWrapper>
  );
};

export default AnimatedCircles; 